import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

const StandardLoginForm = ({ onToggleMethod }) => {
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.identifier) {
      newErrors.identifier = 'Email or Username is required';
    }
    
    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Try to login with identifier (email or username)
      // First, check if it's an email format
      const emailRegex = /\S+@\S+\.\S+/;
      const isEmail = emailRegex?.test(formData?.identifier);
      let loginEmail = formData?.identifier;
      
      // If not email format, it's a username - use secure function to get email
      if (!isEmail) {
        // Use the secure database function to lookup email from username
        const { data: emailData, error: lookupError } = await supabase?.rpc('get_email_from_username', { p_username: formData?.identifier });
        
        if (lookupError || !emailData) {
          setErrors({ submit: 'Invalid username or password' });
          return;
        }
        
        loginEmail = emailData;
      }
      
      // Now attempt login with email
      const { data, error } = await signIn(loginEmail, formData?.password);
      
      if (error) {
        setErrors({ submit: error?.message || 'Login failed. Please try again.' });
        return;
      }

      if (data?.user) {
        // Get user profile to determine role-based redirect
        const { data: profile } = await supabase?.from('user_profiles')?.select('role')?.eq('id', data?.user?.id)?.single();

        // Redirect based on user role
        const userRole = profile?.role || 'employee';
        if (userRole === 'admin' || userRole === 'hr_manager') {
          navigate('/admin-dashboard');
        } else {
          navigate('/employee-dashboard');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {errors?.submit && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          {errors?.submit}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Input
            label="Email or Username"
            type="text"
            name="identifier"
            placeholder="Enter your email or username"
            value={formData?.identifier}
            onChange={handleChange}
            error={errors?.identifier}
            required
            disabled={loading}
          />
        </div>
        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Enter your password"
            value={formData?.password}
            onChange={handleChange}
            error={errors?.password}
            required
            disabled={loading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
            disabled={loading}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={loading}
          iconName="LogIn"
          iconPosition="right"
        >
          Sign In
        </Button>
      </form>
    </div>
  );
};

export default StandardLoginForm;